import api from './api';
import type {
  FacilityMasterIssue, FacilityMasterItem, FacilityMasterItemDraft, FacilityMasterVersion, FacilityMasterWorkspace,
} from '../types/prodFacilityMaster';

const ROOT = '/prod/facility-master-catalogs';
type Raw = Record<string, unknown>;
const record = (value: unknown): Raw => value && typeof value === 'object' && !Array.isArray(value) ? value as Raw : {};
const data = (value: unknown): unknown => record(value).data ?? value;
const string = (value: unknown): string => value === null || value === undefined ? '' : String(value);
const nullable = (value: unknown): string | null => value === null || value === undefined ? null : String(value);
const number = (value: unknown): number => Number(value ?? 0);
const object = (value: unknown): Record<string, unknown> => record(value);

const mapVersion = (rawValue: unknown): FacilityMasterVersion => {
  const raw = record(rawValue);
  return {
    id: string(raw.id), facilityCode: string(raw.facilityCode ?? raw.facility_code), versionNo: number(raw.versionNo ?? raw.version_no),
    name: string(raw.name ?? raw.versionName ?? raw.version_name), status: string(raw.status ?? raw.versionStatus ?? raw.version_status) as FacilityMasterVersion['status'],
    revision: number(raw.revision), validatedRevision: raw.validatedRevision === null || raw.validated_revision === null ? null : number(raw.validatedRevision ?? raw.validated_revision),
    baseVersionId: nullable(raw.baseVersionId ?? raw.base_version_id), createdAt: nullable(raw.createdAt ?? raw.created_at),
    updatedAt: nullable(raw.updatedAt ?? raw.updated_at), publishedAt: nullable(raw.publishedAt ?? raw.published_at),
  };
};
const mapItem = (rawValue: unknown): FacilityMasterItem => {
  const raw = record(rawValue);
  return {
    id: string(raw.id), itemId: string(raw.itemId ?? raw.item_id), type: string(raw.type ?? raw.itemType ?? raw.item_type) as FacilityMasterItem['type'],
    code: string(raw.code ?? raw.itemCode ?? raw.item_code), displayName: string(raw.displayName ?? raw.display_name),
    lifecycleStatus: string(raw.lifecycleStatus ?? raw.lifecycle_status) as FacilityMasterItem['lifecycleStatus'], config: object(raw.config ?? raw.config_json),
    note: nullable(raw.note), displayOrder: number(raw.displayOrder ?? raw.display_order),
  };
};
const mapIssue = (rawValue: unknown): FacilityMasterIssue => {
  const raw = record(rawValue);
  return {
    constraintCode: string(raw.constraintCode ?? raw.constraint_code), severity: string(raw.severity) as FacilityMasterIssue['severity'],
    hardOrSoft: string(raw.hardOrSoft ?? raw.hard_or_soft) as FacilityMasterIssue['hardOrSoft'],
    violationMessageTemplate: string(raw.violationMessageTemplate ?? raw.violation_message_template), violationReason: string(raw.violationReason ?? raw.violation_reason),
    affectedEntities: Array.isArray(raw.affectedEntities ?? raw.affected_entities_json) ? (raw.affectedEntities ?? raw.affected_entities_json) as FacilityMasterIssue['affectedEntities'] : [],
    suggestedAction: raw.suggestedAction === undefined && raw.suggested_action === undefined ? undefined : string(raw.suggestedAction ?? raw.suggested_action),
  };
};
const mapWorkspace = (rawValue: unknown): FacilityMasterWorkspace => {
  const raw = record(data(rawValue));
  return {
    version: mapVersion(raw.version), items: Array.isArray(raw.items) ? raw.items.map(mapItem) : [],
    issues: Array.isArray(raw.issues) ? raw.issues.map(mapIssue) : [],
  };
};

export const prodFacilityMasterApi = {
  async listVersions(facilityCode: string): Promise<FacilityMasterVersion[]> {
    const response = await api.get(ROOT, { params: { facilityCode } });
    const raw = data(response.data);
    return Array.isArray(raw) ? raw.map(mapVersion) : [];
  },
  async createDraft(input: { facilityCode: string; name?: string; baseVersionId?: string }): Promise<FacilityMasterVersion> {
    const response = await api.post(ROOT, input); return mapVersion(data(response.data));
  },
  async getWorkspace(versionId: string): Promise<FacilityMasterWorkspace> {
    const response = await api.get(`${ROOT}/${versionId}/workspace`); return mapWorkspace(response.data);
  },
  async createItem(versionId: string, revision: number, draft: FacilityMasterItemDraft): Promise<{ version: FacilityMasterVersion; item: FacilityMasterItem }> {
    const response = await api.post(`${ROOT}/${versionId}/items`, { revision, ...draft }); const raw = record(data(response.data));
    return { version: mapVersion(raw.version), item: mapItem(raw.item) };
  },
  async updateItem(versionId: string, itemId: string, revision: number, patch: Partial<Omit<FacilityMasterItemDraft, 'type' | 'code'>>): Promise<{ version: FacilityMasterVersion; item: FacilityMasterItem }> {
    const response = await api.patch(`${ROOT}/${versionId}/items/${itemId}`, { revision, ...patch }); const raw = record(data(response.data));
    return { version: mapVersion(raw.version), item: mapItem(raw.item) };
  },
  async retireItem(versionId: string, itemId: string, revision: number): Promise<FacilityMasterVersion> {
    const response = await api.post(`${ROOT}/${versionId}/items/${itemId}/retire`, { revision }); return mapVersion(record(data(response.data)).version);
  },
  async validate(versionId: string, revision: number): Promise<{ version: FacilityMasterVersion; issues: FacilityMasterIssue[] }> {
    const response = await api.post(`${ROOT}/${versionId}/validate`, { revision }); const raw = record(data(response.data));
    return { version: mapVersion(raw.version), issues: Array.isArray(raw.issues) ? raw.issues.map(mapIssue) : [] };
  },
  async publish(versionId: string, revision: number): Promise<FacilityMasterVersion> {
    const response = await api.post(`${ROOT}/${versionId}/publish`, { revision }); return mapVersion(data(response.data));
  },
  async abandon(versionId: string, revision: number): Promise<FacilityMasterVersion> {
    const response = await api.post(`${ROOT}/${versionId}/abandon`, { revision }); return mapVersion(data(response.data));
  },
};

export const facilityMasterErrorMessage = (error: unknown): string => {
  const response = record(record(error).response); const body = record(response.data);
  return string(body.message || record(error).message || '请求失败，请稍后重试。');
};
